import { Document, Font, Page, Text, View } from '@react-pdf/renderer';
import format from 'date-fns/format';
import { FC, useMemo } from 'react';
import { PoDetailsObject } from 'src/services/usePoDetailsApi';
import { PoHistory } from 'src/services/usePoHistoryApi';
import { IVarianceDetails } from 'src/types/VarianceDetails';
import font from '../../assets/fonts/Roboto-Bold.ttf';
import { styles } from './PDFStyles';

const formatDate = (date: string) => (date ? format(new Date(date), 'MM/dd/yyyy') : '');

Font.register({
  family: 'Roboto',
  src: font,
  fonts: [
    { src: font },
    {
      src: font,
      fontWeight: 700,
    },
  ],
});
const lineItemColumns = [
  {
    title: 'Item #',
    key: 'itemNumber',
    hasBoolean: '',
    hasDollar: false,
    decimalFormat: false,
  },
  {
    title: 'GL Code',
    key: 'glCd',
    hasBoolean: 'hasGlCodeVariance',
    hasDollar: false,
    decimalFormat: false,
  },
  {
    title: 'Total PO Qty',
    key: 'poQty',
    hasBoolean: 'hasQtyVariance',
    hasDollar: false,
    decimalFormat: true,
  },
  {
    title: 'Doc Total Qty',
    key: 'invoicedQuantity',
    hasBoolean: 'hasQtyVariance',
    hasDollar: false,
    decimalFormat: true,
  },
  {
    title: 'Qty Var',
    key: 'poQtyVariance',
    hasBoolean: 'hasQtyVariance',
    hasDollar: false,
    decimalFormat: false,
  },
  {
    title: 'PO UOM',
    key: 'poUom',
    hasBoolean: 'hasUOMVariance',
    hasDollar: false,
    decimalFormat: false,
  },
  {
    title: 'PO Price',
    key: 'poPrice',
    hasBoolean: 'hasSubTotalVariance',
    hasDollar: true,
    decimalFormat: true,
  },
  {
    title: 'PO Sub Total',
    key: 'poSubTotal',
    hasBoolean: 'hasSubTotalVariance',
    hasDollar: true,
    decimalFormat: true,
  },
  {
    title: 'Docs Sub Total',
    key: 'invoicedSubTotal',
    hasBoolean: 'hasSubTotalVariance',
    hasDollar: true,
    decimalFormat: true,
  },
  {
    title: '$ Var',
    key: 'poSubTotalVariance',
    hasBoolean: 'hasSubTotalVariance',
    hasDollar: true,
    decimalFormat: true,
  },
] as const;

const lineItemSubChildColumns = [
  {
    title: 'Accounting Period',
    key: 'acctPeriod',
    hasBoolean: '',
    hasDollar: false,
    decimalFormat: false,
  },
  {
    title: 'Doc type',
    key: 'documentType',
    hasBoolean: '',
    hasDollar: false,
    decimalFormat: false,
  },
  {
    title: 'Doc #',
    key: 'documentNumber',
    hasBoolean: '',
    hasDollar: false,
    decimalFormat: false,
  },
  {
    title: 'GL Code',
    key: 'glCode',
    hasBoolean: 'hasGlCodeVariance',
    hasDollar: false,
    decimalFormat: false,
  },
  {
    title: 'Doc Qty',
    key: 'quantity',
    hasBoolean: 'hasQtyVariance',
    hasDollar: false,
    decimalFormat: false,
  },
  {
    title: 'Doc UOM',
    key: 'uom',
    hasBoolean: 'hasUOMVariance',
    hasDollar: false,
    decimalFormat: false,
  },
  {
    title: 'Doc Price',
    key: 'price',
    hasBoolean: 'hasPriceVariance',
    hasDollar: true,
    decimalFormat: true,
  },
  {
    title: 'Doc Subtotal',
    key: 'invoiceSubtotal',
    hasBoolean: 'hasSubTotalVariance',
    hasDollar: true,
    decimalFormat: true,
  },
] as const;

const historyColumns = [
  { title: 'Action', key: 'action' },
  { title: 'Date', key: 'actionDateFormatted' },
  { title: 'Document', key: 'document' },
] as const;

const glCodesColumns = [
  { title: 'Gl Code', key: 'glCdDescription', hasDollar: false, decimalFormat: false },
  { title: 'PO Amt', key: 'poTaxes', hasDollar: true, decimalFormat: true },
  { title: 'Inv Amt', key: 'invoicedTaxes', hasDollar: true, decimalFormat: true },
  { title: 'Tax Var', key: 'taxVariance', hasDollar: true, decimalFormat: true },
  { title: 'PO Amt', key: 'poShipping', hasDollar: true, decimalFormat: true },
  { title: 'Inv Amt', key: 'invoicedShipping', hasDollar: true, decimalFormat: true },
  { title: 'Shipping Var', key: 'shippingVariance', hasDollar: true, decimalFormat: true },
  { title: 'PO Amt', key: 'poDiscount', hasDollar: true, decimalFormat: true },
  { title: 'Inv Amt', key: 'invoicedDiscount', hasDollar: true, decimalFormat: true },
  { title: 'Discount Var', key: 'discountVariance', hasDollar: true, decimalFormat: true },
  { title: 'Total Var', key: 'totalVariance', hasDollar: true, decimalFormat: true },
] as const;

export interface PDFProps {
  isHistoryIncluded: boolean;
  isNotesIncluded: boolean;
  title: string;
  variances: IVarianceDetails;
  varianceId: string;
  poHistory: PoHistory;
  isMultiplePeriodAssigned: boolean;
  poDetails: PoDetailsObject;
}

const PDF: FC<PDFProps> = ({
  isHistoryIncluded,
  isNotesIncluded,
  title,
  variances,
  varianceId,
  poHistory,
  isMultiplePeriodAssigned,
  poDetails,
}) => {
  const buyerDetails = poDetails?.buyer;
  const supplierDetails = poDetails?.supplier;
  const lineItemsVariance = variances.lineItemVariances;

  const varianceGlCodes = variances.glVariances;

  const invoiceInfoColumns = useMemo(
    () => [
      { title: 'PO #', value: variances?.poNum },
      {
        title: 'Accounting Period',
        value: isMultiplePeriodAssigned
          ? 'Multiple Periods Assigned'
          : `${formatDate(variances?.budgetPeriodStartDate)} - ${formatDate(
              variances?.budgetPeriodEndDate
            )}`,
      },
    ],
    [variances, isMultiplePeriodAssigned]
  );

  const invoiceInfoColumns2 = useMemo(
    () => [
      { title: 'Discount', value: `$ ${variances?.discount}` },
      { title: 'Shipping', value: `$ ${variances?.shipping}` },
      { title: 'Taxes', value: `$ ${variances?.taxes}` },
      { title: 'Grand Total', value: `$ ${variances?.grandTotal}` },
    ],
    [variances?.discount, variances?.grandTotal, variances?.shipping, variances?.taxes]
  );

  return (
    <Document>
      <Page size="A4" style={styles.body}>
        <Text style={styles.title}>
          {title} # {varianceId}
        </Text>
        <View style={styles.pageFlow}>
          <View style={{ ...styles.POSection, ...styles.section }}>
            <View style={styles.POSectionColumn}>
              {invoiceInfoColumns.map((column) => (
                <View style={styles.poSectionItem} key={column.title}>
                  <Text style={styles.poSectionHeading}>{column.title}</Text>
                  <Text style={styles.poSectionValue}>{column.value}</Text>
                </View>
              ))}
            </View>
            <View style={styles.POSectionColumn}>
              {invoiceInfoColumns2.map((column) => (
                <View style={styles.poSectionItem} key={column.title}>
                  <Text style={styles.poSectionHeading}>{column.title}</Text>
                  <Text style={styles.poSectionValue}>{column.value}</Text>
                </View>
              ))}
            </View>
          </View>
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Contact Information</Text>
            <View style={styles.divider} />

            <View style={styles.contactInfo}>
              <View style={styles.contactInfoSection}>
                <Text style={styles.subTitle}>Buyer Details:</Text>
                <Text style={styles.description}>{buyerDetails?.name}</Text>
                <Text style={styles.description}>{buyerDetails?.companyName}</Text>
                <Text style={styles.description}>{buyerDetails?.addressLine1}</Text>
                <Text style={styles.description}>{buyerDetails?.addressLine2}</Text>
                <Text style={styles.description}>{buyerDetails?.city}</Text>
                <Text style={styles.description}>{buyerDetails?.state}</Text>
                <Text style={styles.description}>{buyerDetails?.zipCode}</Text>
                <Text style={styles.description}>Phone: {buyerDetails?.phone}</Text>
                <Text style={styles.description}>Email: {buyerDetails?.email}</Text>
                <Text style={styles.description}>Fax: {buyerDetails?.fax}</Text>
              </View>
              <View style={styles.contactInfoSection}>
                <Text style={styles.subTitle}>Supplier Details:</Text>
                <Text style={styles.description}>{supplierDetails?.name}</Text>
                <Text style={styles.description}>{supplierDetails?.addressLine1}</Text>
                <Text style={styles.description}>{supplierDetails?.addressLine2}</Text>
                <Text style={styles.description}>{supplierDetails?.city}</Text>
                <Text style={styles.description}>{supplierDetails?.state}</Text>
                <Text style={styles.description}>{supplierDetails?.zipCode}</Text>
                <Text style={styles.description}>Phone: {supplierDetails?.phone}</Text>
                <Text style={styles.description}>Email: {supplierDetails?.email}</Text>
                <Text style={styles.description}>Fax: {supplierDetails?.fax}</Text>
              </View>
            </View>
          </View>

          {isNotesIncluded && (
            <View style={{ ...styles.section }}>
              <Text style={{ ...styles.sectionTitle }}>Notes</Text>
              <View style={styles.divider} />
              <Text style={styles.description}>{variances?.notes || ''}</Text>
            </View>
          )}
          <View>
            <Text style={styles.sectionTitle}>GL codes</Text>
            <View style={styles.divider} />
          </View>

          <View style={styles.table}>
            <View style={styles.tableHeader}>
              {glCodesColumns.map((columnTitle) => (
                <Text style={styles.tableHeaderCell} key={columnTitle.key}>
                  {columnTitle.title}
                </Text>
              ))}
            </View>
            <View style={styles.tableBody}>
              {varianceGlCodes?.map((glCode, rowIndex: number) => (
                <View key={rowIndex} style={styles.tableRow}>
                  {glCodesColumns.map((column, index: number) => (
                    <Text key={index} style={styles.tableCell}>
                      {column?.hasDollar ? '$ ' : ''}
                      {column?.decimalFormat
                        ? Number(glCode[column.key]).toFixed(2)
                        : glCode[column.key]}
                    </Text>
                  ))}
                </View>
              ))}
            </View>
          </View>

          <View>
            <Text style={styles.sectionTitle}>Line Items</Text>
            <View style={styles.divider} />
          </View>

          <View style={styles.LineItemsTable}>
            <View style={styles.lineItemsTableBody}>
              <>
                {lineItemsVariance?.map((lineItem: any, rowIndex: number) => (
                  <View key={rowIndex} style={{ marginBottom: '20px' }}>
                    <View style={styles.tableHeader}>
                      {lineItemColumns?.map((columnTitle: any) => (
                        <Text style={styles.tableHeaderCell} key={columnTitle.key}>
                          {columnTitle.title}
                          {lineItem[columnTitle.hasBoolean] ? '*' : ''}
                        </Text>
                      ))}
                    </View>
                    <View style={styles.lineItemsTableRow}>
                      {lineItemColumns?.map((column: any, index: number) => (
                        <Text key={index} style={styles.tableCell}>
                          {column?.hasDollar ? '$ ' : ''}
                          {column?.decimalFormat
                            ? Number(lineItem[column.key]).toFixed(2)
                            : lineItem[column.key]}
                          {lineItem[column.hasBoolean] ? '*' : ''}
                        </Text>
                      ))}
                    </View>
                    <View key={rowIndex} style={{ paddingLeft: '1px' }}>
                      <View style={styles.tableHeader}>
                        {lineItemSubChildColumns?.map((column, index: number) => (
                          <Text
                            key={column.key}
                            style={
                              index === 0 ? styles.tableHeaderFirstCell : styles.tableHeaderCell
                            }
                          >
                            {column.title}
                          </Text>
                        ))}
                      </View>
                      <View style={{ marginTop: '1px' }}>
                        {lineItem?.periodSummaries?.length > 0 ? (
                          lineItem?.periodSummaries?.map((childRow: any, childIndex: number) =>
                            childRow?.invoices?.map((subChildRow: any, subChildIndex: number) => (
                              <View key={rowIndex} style={styles.lineItemsTableRow}>
                                {lineItemSubChildColumns.map((column: any, index: number) => (
                                  <>
                                    {lineItemColumns?.length > 0 ? (
                                      <Text
                                        key={index}
                                        style={
                                          index === 0
                                            ? styles.tableRowFirstCell
                                            : styles.tableRowCell
                                        }
                                      >
                                        {column?.hasDollar ? ' $' : ''}
                                        {column?.decimalFormat
                                          ? Number(subChildRow[column.key]).toFixed(2)
                                          : subChildRow[column.key]}
                                        {subChildRow[column.hasBoolean] ? ' *' : ''}
                                      </Text>
                                    ) : (
                                      <Text style={styles.noRecords}>No records available</Text>
                                    )}
                                  </>
                                ))}
                              </View>
                            ))
                          )
                        ) : (
                          <Text style={styles.noRecords}>No records available....</Text>
                        )}
                      </View>
                    </View>
                  </View>
                ))}
              </>
            </View>
          </View>

          {isHistoryIncluded && (
            <View>
              <View>
                <Text style={styles.sectionTitle}>{title} History</Text>
                <View style={styles.divider} />
              </View>

              <View style={styles.table}>
                <View style={styles.tableHeader}>
                  {historyColumns.map((column) => (
                    <Text style={styles.tableHeaderCell} key={column.key}>
                      {column.title}
                    </Text>
                  ))}
                </View>
                <View style={styles.tableBody}>
                  {poHistory?.map((historyItem, rowIndex: number) => (
                    <View key={rowIndex} style={styles.tableRow}>
                      {historyColumns.map((column) => (
                        <Text key={column.key} style={styles.tableCell}>
                          {historyItem[column.key]}
                        </Text>
                      ))}
                    </View>
                  ))}
                </View>
              </View>
            </View>
          )}
        </View>
        <Text
          style={styles.pageNumber}
          render={({ pageNumber, totalPages }) => `Page ${pageNumber} / ${totalPages}`}
          fixed
        />
      </Page>
    </Document>
  );
};

export default PDF;
